// Master Yi - Alpha Strike: blinks to the target dealing 4 rapid slices, each 1/4 damage.
// After the 4 slices it chains to one adjacent enemy and can chain once more (up to 3 targets).
// Yi is immune to all damage while in Alpha Strike (alphaStrikeTurnsRemaining > 0).

import { physicalDamage } from "../../combat/DamageCalc";
import { effectiveStats } from "../UnitStats";
import { hexNeighbors } from "../../engine/hex/HexCoord";
import type { Ability, AbilityContext, AbilityHit } from "./Ability";
import type { Unit } from "../Unit";
import type { CombatContext } from "../../combat/CombatContext";

const SLICES = 4;
// VFX timing constants (kept in sync with SlashVFX.ts)
const SLASH_INTERVAL  = 75;  // ms between each slash start
const SLASH_DURATION  = 170; // ms each slash is visible
const TARGET_GAP      = 110; // ms pause between targets
const PER_TARGET_MS   = SLASH_INTERVAL * (SLICES - 1) + SLASH_DURATION; // 395ms
const IMMUNITY_BUFFER = 350; // ms padding after last slash fades

function alphaStrikeDuration(numTargets: number): number {
  const ms = numTargets * PER_TARGET_MS + (numTargets - 1) * TARGET_GAP + IMMUNITY_BUFFER;
  return ms / 1000;
}

function baseDamage(caster: Unit): number {
  const stats = effectiveStats(caster);
  return physicalDamage(stats.ad * 1.8 + 70);
}

function firstAdjacentEnemy(
  fromUnit: Unit,
  casterTeam: "player" | "enemy",
  combat: CombatContext,
  exclude: Unit[],
): Unit | undefined {
  return hexNeighbors(fromUnit.pos)
    .map((h) => combat.unitAt(h))
    .find((u): u is Unit => !!u && u.team !== casterTeam && !exclude.includes(u));
}

function slices(caster: Unit, target: Unit): AbilityHit[] {
  const total = baseDamage(caster);
  const perSlice = total / SLICES;
  return Array.from({ length: SLICES }, () => ({
    target,
    amount: perSlice,
    type: "physical" as const,
    suppressPopup: true,
    alphaSlice: true,
  }));
}

export const masterYiAlphaStrike: Ability = {
  id: "masterYiAlphaStrike",
  displayName: "Alpha Strike",
  description:
    "Yi blinks through up to 3 enemies delivering 4 rapid strikes each. Immune to all damage while striking.",
  iconId: "AlphaStrike",

  targetShape({ caster, target, combat }: AbilityContext) {
    const shape = [caster.pos, target.pos];
    const spread1 = firstAdjacentEnemy(target, caster.team, combat, [target]);
    if (spread1) {
      shape.push(spread1.pos);
      const spread2 = firstAdjacentEnemy(spread1, caster.team, combat, [target, spread1]);
      if (spread2) shape.push(spread2.pos);
    }
    return shape;
  },

  computeHits({ caster, target, combat }: AbilityContext): AbilityHit[] {
    const spread1 = firstAdjacentEnemy(target, caster.team, combat, [target]);
    const spread2 = spread1
      ? firstAdjacentEnemy(spread1, caster.team, combat, [target, spread1])
      : null;
    const numTargets = 1 + (spread1 ? 1 : 0) + (spread2 ? 1 : 0);

    const hits: AbilityHit[] = [
      // Self-immunity for the duration of the strike
      {
        target: caster,
        amount: 0,
        type: "none",
        effect: { kind: "alphaStrike", duration: 1 },
      },
      ...slices(caster, target),
    ];

    if (spread1) hits.push(...slices(caster, spread1));
    if (spread2) hits.push(...slices(caster, spread2));

    return hits;
  },
};
